import path from 'path';
import fs from 'fs';
import { logger } from '../utils/logger';

/**
 * Schema class for managing database schema operations
 */
export class Schema {
  private schemaFilePath: string;
  private cachedSchema: any = null;

  constructor(schemaFilePath?: string) {
    this.schemaFilePath = schemaFilePath || path.join(process.cwd(), '../analysis/data/schema.json');
  }

  /**
   * Gets the database schema from the schema file
   * @returns Parsed schema object or null if error occurs
   */
  getDatabaseSchema(): any | null {

    try {
      // Create directory structure if it doesn't exist
      const dir = path.dirname(this.schemaFilePath);
      if (!fs.existsSync(dir)) {
        logger.info(`Creating directory structure: ${dir}`);
        fs.mkdirSync(dir, { recursive: true });
      }

      // Create file with empty schema if it doesn't exist
      if (!fs.existsSync(this.schemaFilePath)) {
        logger.info(`Schema file does not exist at ${this.schemaFilePath}, creating with empty schema`);
        const initialSchema = {
          database: '',
          schema: '',
          description: '',
          tables: [],
          relationships: []
        };
        fs.writeFileSync(this.schemaFilePath, JSON.stringify(initialSchema, null, 4));
        this.cachedSchema = initialSchema;
        return initialSchema;
      }

      const fileContent = fs.readFileSync(this.schemaFilePath, 'utf-8');
      const schema = JSON.parse(fileContent);
      this.cachedSchema = schema;
      return schema;
    } catch (error) {
      logger.error('Error parsing schema file:', error);
      return null;
    }
  }

  /**
   * Gets the cached schema or loads it if not cached
   * @returns Cached schema or freshly loaded schema
   */
  getSchema(): any | null {
    if (this.cachedSchema) {
      return this.cachedSchema;
    }
    return this.getDatabaseSchema();
  }

  /**
   * Generates database schema documentation for LLM from Snowflake JSON schema
   * @returns Formatted schema documentation string
   */
  generateSchemaDocumentation(): string {
    const schema = this.getSchema();

    if (!schema) {
      logger.warn('No database schema found.');
      return 'No database schema available.';
    }

    const tables: string[] = [];

    // Header information
    tables.push(`Database: ${schema.database}`);
    tables.push(`Schema: ${schema.schema}`);
    tables.push(`Description: ${schema.description}`);
    tables.push('');
    tables.push('='.repeat(80));
    tables.push('');

    // Process each table
    for (const table of schema.tables) {
      const tableInfo: string[] = [];

      tableInfo.push(`TABLE: ${table.fullName}`);
      tableInfo.push(`Description: ${table.description}`);
      tableInfo.push(`Row Count: ~${table.rowCount.toLocaleString()}`);
      tableInfo.push('');
      tableInfo.push('Columns:');

      // Process columns
      for (const column of table.columns) {
        let columnLine = `  - ${column.name}: ${column.type}`;

        if ((column as any).isPrimaryKey) {
          columnLine += ' (PRIMARY KEY)';
        }

        if ((column as any).isForeignKey && (column as any).references) {
          columnLine += ` (FK -> ${(column as any).references.table}.${(column as any).references.column})`;
        }

        if (!column.nullable) {
          columnLine += ' NOT NULL';
        }

        if (column.description) {
          columnLine += ` - ${column.description}`;
        }

        tableInfo.push(columnLine);

        // Add value examples for categorical columns
        if ((column as any).sampleValues && (column as any).sampleValues.length > 0) {
          tableInfo.push(`    Sample values: [${(column as any).sampleValues.join(', ')}]`);
        }

        // Add statistics if available
        if ((column as any).statistics) {
          const stats = (column as any).statistics;
          if (stats.min !== undefined && stats.max !== undefined) {
            tableInfo.push(`    Range: ${stats.min} to ${stats.max}`);
          }
          if (stats.distinct !== undefined) {
            tableInfo.push(`    Distinct values: ${stats.distinct.toLocaleString()}`);
          }
        }
      }

      tableInfo.push('');
      tables.push(tableInfo.join('\n'));
    }

    // Add relationships section
    tables.push('='.repeat(80));
    tables.push('');
    tables.push('TABLE RELATIONSHIPS:');
    tables.push('');

    for (const rel of schema.relationships) {
      tables.push(`${rel.from} -> ${rel.to} (${rel.type}): ${rel.keys.join(' = ')}`);
    }

    return tables.join('\n');
  }

  /**
   * Clears the cached schema, forcing a reload on next access
   */
  clearCache(): void {
    this.cachedSchema = null;
  }

  /**
   * Sets a custom schema file path
   * @param filePath - Path to the schema file
   */
  setSchemaPath(filePath: string): void {
    this.schemaFilePath = filePath;
    this.clearCache();
  }
}

// Export a singleton instance for use across the application
export const schema = new Schema();
